/**
 * Intake for emailed auth links - the ONE place the app reads
 * window.location's path + query string.
 *
 * The backend emails links like  {APP_BASE_URL}/reset-password?token=...
 * (see backend/identity/routes_auth.py), but this SPA navigates purely via the URL
 * hash (hashNav.js) - the SPA fallback serves index.html for any path and
 * nothing ever looked at pathname or search, so recipients landed on the
 * login screen with their token silently discarded. This module recognizes
 * the three link shapes at boot and hands App.jsx a {kind, token} to render
 * the matching screen (auth/AuthLinkScreen.jsx); consumeAuthLink() then
 * scrubs the URL so a refresh doesn't re-run the flow and the token doesn't
 * linger in the address bar / browser history.
 *
 * Deliberately not a router, same reasoning as hashNav.js - three fixed
 * paths, read once at boot. If a real router ever lands, fold these paths
 * into it and delete this file; only App.jsx knows it exists.
 */

const PATH_TO_KIND = {
  '/reset-password': 'reset',
  '/accept-invite': 'invite',
  '/verify-email': 'verify',
  // Public self-service signup (ROADMAP.md §2) - routes_auth.py's
  // /signup/verify emails this link; unlike the three above, clicking it
  // creates a brand-new org (see auth/SignupVerifyScreen.jsx), so it needs
  // an org name from the person before it can complete - same reason
  // '/google/new-org' below isn't a plain AuthLinkScreen kind either.
  '/signup-verify': 'signup_verify',
  // Google SSO (ROADMAP.md §2) - routes_sso.py redirects here (not a fetch, a
  // real page load) when a verified Google identity needs a human decision
  // it can't make mid-redirect: which of several matching orgs, or whether
  // to spin up a brand-new one. `ticket` (not `token`) carries that
  // decision's context - see auth/GoogleLandingScreen.jsx.
  '/google/pick-org': 'google_pick_org',
  '/google/new-org': 'google_new_org',
  // Finding #4: an MFA-enrolled account signing in via Google still needs
  // its second factor - routes_sso.py redirects here instead of completing
  // the login inline, same reason the two paths above exist.
  '/google/mfa': 'google_mfa_pending',
  // Microsoft SSO - identical shape to the three Google paths above, its
  // own parallel set of routes/screens (see identity/routes_sso.py's own
  // note on why Microsoft isn't folded into the Google ones).
  '/microsoft/pick-org': 'microsoft_pick_org',
  '/microsoft/new-org': 'microsoft_new_org',
  '/microsoft/mfa': 'microsoft_mfa_pending',
  // Company (per-org SAML/OIDC) sign-in: the MFA step is the same ticket-for-code exchange, so it reuses the Microsoft
  // landing screen's provider-agnostic MFA view (identity/routes_enterprise_sso.py).
  '/sso/mfa': 'microsoft_mfa_pending',
};

export function readAuthLink() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  const kind = PATH_TO_KIND[path];
  if (!kind) return null;
  const params = new URLSearchParams(window.location.search);
  // Both SSO providers' redirects carry `ticket`, not `token` - see
  // GoogleLandingScreen.jsx/MicrosoftLandingScreen.jsx.
  const isSso = kind.startsWith('google_') || kind.startsWith('microsoft_');
  const token = params.get(isSso ? 'ticket' : 'token') || '';
  return { kind, token };
}

/** routes_sso.py redirects a failed Google sign-in to `/?sso_error=...`
 * instead of a dedicated path (it's an error ON the login screen, not a
 * separate screen) - read once at boot, same scrub-after-read contract as
 * consumeAuthLink(). */
/**
 * `/f/<token>` — a tokenised form link
 * (docs/tokenised-form-links-scoping-2026-09.md Phase 1). Lives here because
 * this module is the one place the app reads `window.location`'s path, but it
 * is NOT an auth link and App.jsx treats it as neither: there is no session
 * to establish and nothing to consume, so the URL is deliberately left alone
 * (the link is single-use server-side, and scrubbing the token would break a
 * refresh mid-typing for no gain the server doesn't already give us).
 *
 * The token is in the PATH rather than a query param because a query string
 * is what a link previewer, an analytics redirector and a copied-into-chat
 * URL are most likely to mangle or strip.
 */
export function readFormLink() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  const m = /^\/f\/([A-Za-z0-9_-]{16,128})$/.exec(path);
  return m ? m[1] : null;
}

// `/s/<token>` — a tokenised SHARE link, the read-only twin of `/f/` above.
// Same shape, same token grammar, deliberately a different path: the two
// pages have opposite postures (one collects, one publishes) and a recipient
// glancing at a URL should be able to tell which they were sent.
export function readShareLink() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  const m = /^\/s\/([A-Za-z0-9_-]{16,128})$/.exec(path);
  return m ? m[1] : null;
}

// `/pi/<key>` — a PUBLIC INTAKE form (docs/public-intake-scoping-2026-10.md): the
// page a customer embeds on their own site, usually in an iframe. The key is a
// public identifier and not a credential (it carries no tenant information and
// the endpoint can only create one row), which is why this is a plain path and
// not a token in a fragment. Branches in main.jsx above the login gate for the
// same reason `/f/` does.
export function readPublicIntake() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  const m = /^\/pi\/([A-Za-z0-9_-]{8,64})$/.exec(path);
  return m ? m[1] : null;
}

// `/survey/<token>` is a survey invite (anonymous-responses P2b): the fourth page
// that branches in main.jsx above the login gate. The token is the whole authority.
export function readSurveyInvite() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  const m = /^\/survey\/([A-Za-z0-9_-]{16,128})$/.exec(path);
  return m ? m[1] : null;
}

export function readSsoError() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path !== '/') return '';
  return new URLSearchParams(window.location.search).get('sso_error') || '';
}

export function consumeAuthLink() {
  window.history.replaceState(null, '', '/');
}
